/**
 * Shared validation schemas using Zod
 * Used for form validation and API request/response validation
 */

import { z } from 'zod';

// ============================================
// Common validation patterns
// ============================================

/**
 * Turkish phone number validation
 * Formats: 5xxxxxxxxx, 05xxxxxxxxx, +905xxxxxxxxx
 */
export const phoneSchema = z
  .string()
  .regex(
    /^(\+90|0)?5\d{9}$/,
    'Geçerli bir telefon numarası girin (örn: 5xx xxx xx xx)'
  )
  .optional()
  .or(z.literal(''));

/**
 * Turkish tax number (Vergi No) validation - 10 digits
 */
export const taxNumberSchema = z
  .string()
  .regex(/^\d{10}$/, 'Vergi numarası 10 haneli olmalıdır')
  .optional()
  .or(z.literal(''));

/**
 * Turkish ID number (TC Kimlik No) validation - 11 digits
 */
export const tcKimlikSchema = z
  .string()
  .regex(/^[1-9]\d{10}$/, 'TC Kimlik numarası 11 haneli olmalıdır ve 0 ile başlamamalıdır')
  .optional()
  .or(z.literal(''));

/**
 * Email validation with Turkish message
 */
export const emailSchema = z
  .string()
  .email('Geçerli bir e-posta adresi girin');

/**
 * Password validation with Turkish message
 */
export const passwordSchema = z
  .string()
  .min(8, 'Şifre en az 8 karakter olmalıdır')
  .regex(/[A-Z]/, 'Şifre en az bir büyük harf içermelidir')
  .regex(/[a-z]/, 'Şifre en az bir küçük harf içermelidir')
  .regex(/[0-9]/, 'Şifre en az bir rakam içermelidir');

/**
 * Simple password (no complexity requirements)
 */
export const simplePasswordSchema = z
  .string()
  .min(6, 'Şifre en az 6 karakter olmalıdır');

/**
 * Required string with Turkish message
 */
export const requiredString = (fieldName: string) =>
  z.string().min(1, `${fieldName} zorunludur`);

/**
 * Optional string that converts empty to undefined
 */
export const optionalString = z
  .string()
  .optional()
  .transform((val) => (val === '' ? undefined : val));

/**
 * Positive number validation
 */
export const positiveNumber = z
  .number()
  .positive('Değer 0\'dan büyük olmalıdır');

/**
 * Non-negative number validation
 */
export const nonNegativeNumber = z
  .number()
  .min(0, 'Değer negatif olamaz');

/**
 * Percentage validation (0-100)
 */
export const percentageSchema = z
  .number()
  .min(0, 'Yüzde 0\'dan küçük olamaz')
  .max(100, 'Yüzde 100\'den büyük olamaz');

// ============================================
// Common form schemas
// ============================================

/**
 * Login form schema
 */
export const loginFormSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Şifre zorunludur'),
  remember: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginFormSchema>;

/**
 * Change password form schema
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Mevcut şifre zorunludur'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Şifre tekrarı zorunludur'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Şifreler eşleşmiyor',
    path: ['confirmPassword'],
  });

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

/**
 * Date range schema (for reports)
 */
export const dateRangeSchema = z.object({
  startDate: z.string().min(1, 'Başlangıç tarihi zorunludur'),
  endDate: z.string().min(1, 'Bitiş tarihi zorunludur'),
}).refine(
  (data) => new Date(data.startDate) <= new Date(data.endDate),
  { message: 'Başlangıç tarihi bitiş tarihinden sonra olamaz', path: ['startDate'] }
);

export type DateRangeData = z.infer<typeof dateRangeSchema>;

// ============================================
// Utility functions
// ============================================

/**
 * Validate data against a schema and return errors as a simple object
 */
export function validateWithSchema<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!errors[path]) {
      errors[path] = issue.message;
    }
  }

  return { success: false, errors };
}
